import {
  ButtonColor,
  ButtonSize,
  Modal,
  ModalType,
  Option,
  Table,
  TableRow,
  TextInput,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import {
  ClientInfoTaxPageValidation,
  Customer,
  CustomerTaxInfo,
  Document,
} from 'src/front/api/apiInstance';
import {
  CardApplicationScreenType,
  ReferenceDataType,
  ScreenProcessType,
} from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import { convertStringToDate, formatDate } from 'src/front/common/utils/dateTimeUtils';

import {
  dictionaryLineFormat,
  formatNumeric,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import { TaxNumberSchema } from 'src/front/common/utils/validationSchemas';
import AsyncButton from 'src/front/components/AsyncButton';

import {
  Container,
  FormElement,
  FormRow,
  InfoLabel,
  InfoRow,
  LineSeparator,
  Separator,
  Subtitle,
  TableWrapper,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import { IdentityFormData } from 'src/front/components/identity/IdentityFormData';
import { useCustomerSearchState } from 'src/front/contexts/ClientInfoContext';
import TaxModal, {
  TaxFormData,
} from 'src/front/pages/applications/card-entry/tax-information/TaxModal';
import uid from 'uid';
import IdentityEntry from 'src/front/components/identity/IdentityEntry';

const taxTableHeaders = [
  'changeFundClientInfo_taxResidenceCountry_label',
  'changeFundClientInfo_iinnProvided_label',
  'changeFundClientInfo_iinn_label',
];

interface Props {
  goToNextStep: () => void;
  isEdit?: boolean;
  isEditCustomerName?: string;
}

const defaultIdentityValue = {
  identityType: null,
  identityDocumentSerialCode: '',
  identityDocumentSerialNumber: '',
  issueDate: null,
  expireDate: null,
  issuedBy: '',
  issueCountry: null,
  unitCode: '',
};

interface Identity extends IdentityFormData {
  isNew?: boolean;
  givenCountry?: string;
}

interface FormData {
  taxNumber: string;
}

const defaultValues = {
  taxNumber: '',
};

type Tax = TaxFormData;

const DocumentTaxInformation: FC<Props> = ({ goToNextStep, isEdit, isEditCustomerName }) => {
  const { t } = useTranslation();

  const { customerId, clientName } = useCustomerSearchState();
  const [warning, setWarning] = useState<string | undefined>('');
  const [identities, setIdentities] = useState<Identity[]>([]);
  const [identityTypes, setIdentityTypes] = useState<Option[]>([]);
  const [isLoading, setIsloading] = useState<boolean>(true);
  const [showTaxModal, setShowTaxModal] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [taxFormValues, setTaxFormValues] = useState<Tax>();
  const [selectedTax, setSelectedTax] = useState<TableRow>();
  const [loadingTable, setLoadingTable] = useState<boolean>(true);
  const [isClientDataChangedWarning, setIsClientDataChangedWarning] = useState<string>('');

  const countries = usePRM(ReferenceDataType.COUNTRIES);
  const yesNoComboItems = usePRM(ReferenceDataType.ISPROVIDED);

  const { errors, setValue, control, watch } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(TaxNumberSchema),
  });

  useEffect(() => {
    (async () => {
      const { data } = await Document.getDocumentTypeByScreenType({
        cardApplicationScreenType: CardApplicationScreenType.CLIENTINFOCHANGE,
      });

      if (data.result === 'OK') {
        setIdentityTypes(data.documentTypeList.map(optionFormat));
      }
    })();
  }, []);

  const loadTaxInformation = useCallback(async () => {
    const { data } = await ClientInfoTaxPageValidation.getClientInfoTaxPageData();

    if (data.result === 'OK' && data.pageData) {
      const { taxData } = data.pageData;

      const taxList =
        taxData?.taxResidencyList?.map((taxResidency) => ({
          taxResidenceCountry: optionFormat(taxResidency.taxResidenceCountry),
          hasTaxNumberProvided: optionFormat(taxResidency.hasTaxNumberProvided),
          foreignTaxNumberId: taxResidency.foreignTaxNumberId || uid(),
          differentNumber: taxResidency.differentNumber || false,
          foreignTaxNumber: taxResidency.foreignTaxNumber || '',
          isNew: taxResidency.isNew,
        })) || [];
      setValue('taxNumber', taxData?.taxNumber || '');
      setTaxes(taxList);
    } else {
      const { data: response } = await CustomerTaxInfo.getCustomerTaxInfo({
        customerId,
        cardApplicationScreenType: CardApplicationScreenType.CLIENTINFOCHANGE,
      });

      if (response.result === 'OK') {
        const [noOption, yesOption] = yesNoComboItems;

        const taxList =
          response.taxResidencyList?.map((tax) => ({
            taxResidenceCountry:
              countries.find((country) => country.value === tax.taxResidenceCountryCode) || null,
            hasTaxNumberProvided: tax.hasTaxNumberProvided ? yesOption : noOption,
            differentNumber: tax.differentNumber || false,
            foreignTaxNumber: tax.foreignTaxNumber || '',
            foreignTaxNumberId: tax.foreignTaxNumberId,
            isNew: tax.isNew,
          })) || [];

        setValue('taxNumber', response.taxNumber || '');
        setTaxes(taxList);
      }
    }
    setLoadingTable(false);
  }, [countries, customerId, setValue, yesNoComboItems]);

  useEffect(() => {
    if (countries.length && yesNoComboItems.length) {
      loadTaxInformation();
    }
  }, [customerId, countries.length, loadTaxInformation, yesNoComboItems.length]);

  const handleSubmit = async (): Promise<void> => {
    const hasTaxResidency = taxes.length > 0;
    if (!hasTaxResidency) {
      setWarning('general_noTaxResidencyInfo_errorMessage');
    } else {
      setWarning(undefined);

      const { data } = await ClientInfoTaxPageValidation.validateClientInfoTaxPageData({
        taxData: {
          taxNumber: watch('taxNumber') || '',
          taxResidencyList: taxes.map((tax) => ({
            taxResidenceCountry: dictionaryLineFormat(tax.taxResidenceCountry),
            hasTaxNumberProvided: dictionaryLineFormat(tax.hasTaxNumberProvided),
            foreignTaxNumber: tax.foreignTaxNumber,
            differentNumber: tax.differentNumber,
            isNew: tax.isNew || false,
            ...(tax.isNew ? {} : { foreignTaxNumberId: tax.foreignTaxNumberId }),
          })),
        },
        documentIdentityDataList: identities.map((identity) => ({
          ...(identity.isNew ? {} : { customerIdentityId: identity.id }),
          identityType: dictionaryLineFormat(identity.identityType),
          identityDocumentSerialCode: identity.identityDocumentSerialCode,
          identityDocumentSerialNumber: identity.identityDocumentSerialNumber,
          issueDate: formatDate(identity.issueDate),
          expireDate: formatDate(identity.expireDate),
          issuedBy: identity.issuedBy,
          ...(identity.issueCountry
            ? { issueCountry: dictionaryLineFormat(identity.issueCountry) }
            : {}),
          unitCode: identity.unitCode,
          isNew: identity.isNew,
          isActive: identity.isActive,
        })),
        screenProcessType: isEdit ? ScreenProcessType.EDIT : ScreenProcessType.NEW,
      });
      if (data.result === 'OK') {
        isEdit && goToNextStep();
        data.isClientDataChanged
          ? goToNextStep()
          : setIsClientDataChangedWarning('general_thereIsNoChange_errorMessage');
      }
    }
  };

  const loadFormFields = useCallback(async (): Promise<void> => {
    try {
      const { data: response } = await ClientInfoTaxPageValidation.getClientInfoTaxPageData();
      if (response.result === 'OK' && response.pageData) {
        const { documentIdentityDataList } = response.pageData;

        const identityList =
          documentIdentityDataList?.map((identity) => ({
            id: identity.customerIdentityId || uid(),
            identityType: optionFormat(identity.identityType),
            identityDocumentSerialCode: identity.identityDocumentSerialCode || '',
            identityDocumentSerialNumber: identity.identityDocumentSerialNumber || '',
            issueDate: convertStringToDate(identity.issueDate),
            expireDate: convertStringToDate(identity.expireDate),
            issuedBy: identity.issuedBy || '',
            issueCountry: identity.issueCountry ? optionFormat(identity.issueCountry) : null,
            unitCode: identity.unitCode || '',
            isNew: identity.isNew,
            isActive: identity.isActive,
          })) || [];

        setIdentities(identityList);
      } else if (customerId) {
        const { data } = await Customer.getCustomerIdentities({
          customerId,
          cardApplicationScreenType: CardApplicationScreenType.CLIENTINFOCHANGE,
        });

        const identityList =
          data?.customerIdentityList?.map((identity) => ({
            ...defaultIdentityValue,
            identityDocumentSerialCode: identity.series,
            identityDocumentSerialNumber: identity.serialCode,
            issueDate: convertStringToDate(identity.issueDate),
            expireDate: convertStringToDate(identity.expirationDate),
            identityType: identityTypes.find((type) => type.value === identity.documentType) || {
              value: identity.documentType,
              label: identity.documentType,
            },
            id: identity.customerIdentityId,
            issuedBy: identity.issuedBy,
            unitCode: identity.unitCode || '',
            isNew: false,
            issueCountry:
              countries.find((country) => country.value === identity.givenCountry) || null,
            givenCountry: identity.givenCountry,
            isActive: identity.isActive,
          })) || [];
        setIdentities(identityList);
      }
      setIsloading(false);
    } catch (err) {
      // TODO (abdulbasid): show error on modal or handle with error boundary
    }
  }, [countries, customerId, identityTypes]);

  useEffect(() => {
    if (identityTypes.length > 0) {
      loadFormFields();
    }
  }, [identityTypes.length, loadFormFields]);

  const addAddress = useCallback((): void => {
    setTaxFormValues(undefined);
    setShowTaxModal(true);
  }, []);

  const editAddress = useCallback(
    (item?: TableRow): void => {
      setTaxFormValues(taxes.find((tax) => tax.foreignTaxNumberId === item?.id));
      setShowTaxModal(true);
    },
    [taxes]
  );

  const deleteAddress = useCallback((item?: TableRow): void => {
    setIsDeleteModalOpen(true);
  }, []);

  const addressActions = useMemo(
    () => ({
      add: addAddress,
      edit: editAddress,
      delete: deleteAddress,
    }),
    [addAddress, deleteAddress, editAddress]
  );

  const handleTaxSubmit = async (tax: Tax): Promise<void> => {
    let newTaxes;
    if (tax?.foreignTaxNumberId) {
      newTaxes = taxes.map((tx) => (tx.foreignTaxNumberId === tax.foreignTaxNumberId ? tax : tx));
    } else {
      warning && setWarning(undefined);
      newTaxes = [{ ...tax, foreignTaxNumberId: uid(), isNew: true }, ...taxes];
    }
    setTaxes(newTaxes);
    setShowTaxModal(false);
    return Promise.resolve();
  };

  const handleDeleteTax = (): void => {
    setTaxes(taxes.filter((tax) => tax.foreignTaxNumberId !== selectedTax?.id));
    setSelectedTax(undefined);
    setIsDeleteModalOpen(false);
  };

  const closeTaxModal = (): void => setShowTaxModal(false);

  const taxTableData: TableRow[] = taxes.map(
    (tax: Tax): TableRow => ({
      id: tax.foreignTaxNumberId || '',
      columns: [
        tax.taxResidenceCountry?.label || '',
        tax.hasTaxNumberProvided?.label || '',
        tax.foreignTaxNumber,
      ],
    })
  );

  const selectedCountries = taxes.map((tax) => tax.taxResidenceCountry?.value);
  const filteredCountries = countries.filter((country) =>
    taxFormValues
      ? !selectedCountries.includes(country.value) ||
        taxFormValues.taxResidenceCountry?.value === country.value
      : !selectedCountries.includes(country.value)
  );

  return (
    <Container>
      <Title>
        <Trans i18nKey="changeFundClientInfo_title" />
      </Title>

      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="changeFundClientInfo_clientName_text" />:
        </InfoLabel>
        {isEditCustomerName || clientName?.clientName}
      </InfoRow>
      <FormRow>
        <LineSeparator />
      </FormRow>
      <Separator />
      <Subtitle>
        <Trans i18nKey="changeFundClientInfo_documentInfo_subtitle" />
      </Subtitle>
      <IdentityEntry
        identities={identities}
        identityTypes={identityTypes}
        isLoading={isLoading}
        setIdentities={setIdentities}
        isEditable
        showActiveFlag
      />
      <Separator />
      <Title>
        <Trans i18nKey="changeFundClientInfo_taxInfo_subtitle" />
      </Title>
      {isDeleteModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_deleteRecordConfirm_text')}
          onCancel={() => setIsDeleteModalOpen((state) => !state)}
          onSubmit={handleDeleteTax}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      ) : null}
      {showTaxModal ? (
        <TaxModal
          countries={filteredCountries}
          formValues={taxFormValues}
          onClose={closeTaxModal}
          submit={handleTaxSubmit}
        />
      ) : null}

      <FormRow>
        <FormElement>
          <Controller
            control={control}
            name="taxNumber"
            render={({ onChange, value }) => (
              <TextInput
                errorMessage={t(errors?.taxNumber?.message as string)}
                formatter={formatNumeric}
                label={t('changeFundClientInfo_iinn_label')}
                maxLength={12}
                name="taxNumber"
                onChange={onChange}
                value={value}
              />
            )}
          />
        </FormElement>
      </FormRow>
      <Subtitle>
        <Trans i18nKey="changeFundClientInfo_taxInfo_subtitle" />
      </Subtitle>
      <TableWrapper>
        <Table
          actionConfig={addressActions}
          addActionText={t('general_add_button')}
          emptyTableText={t('general_noTaxResidencyInfoTable_text')}
          headers={taxTableHeaders.map((head) => t(head))}
          items={taxTableData}
          loading={loadingTable}
          onSelect={setSelectedTax}
          pageNumber={1}
          selectedItem={selectedTax}
          widths={['50', '30', '20']}
        />
      </TableWrapper>
      <WarningText>
        <Trans i18nKey={warning} />
      </WarningText>
      {isClientDataChangedWarning ? (
        <WarningText>
          <Trans i18nKey={isClientDataChangedWarning} />
        </WarningText>
      ) : null}
      <FormRow>
        <AsyncButton
          color={ButtonColor.Primary}
          onClick={handleSubmit}
          size={ButtonSize.Medium}
          text={t('general_continue_button')}
        />
      </FormRow>
    </Container>
  );
};

export default DocumentTaxInformation;
